import { useEffect, useState } from "react";

const initial = { ready: false, webgl: false, fine: false, reduced: false };

// Singleton — Chromium caps at ~16 WebGL contexts globally and every
// getContext("webgl") counts against the budget even for probe canvases
// that are never attached. Running the probe once at module scope avoids
// starving the R3F stages when the page mounts many LivingImages.
let CACHED = null;
let CACHED_READY = false;
const waiters = new Set();

function detect() {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fine = window.matchMedia("(pointer: fine)").matches;
  const wide = window.matchMedia("(min-width: 1024px)").matches;
  let gl = false;
  try {
    const c = document.createElement("canvas");
    const ctx = c.getContext("webgl2") || c.getContext("webgl");
    gl = !!ctx;
    // release the probe context immediately
    if (ctx && ctx.getExtension) {
      const lose = ctx.getExtension("WEBGL_lose_context");
      lose && lose.loseContext();
    }
  } catch {
    gl = false;
  }
  const lowPower =
    (navigator.hardwareConcurrency || 8) <= 4 ||
    (navigator.deviceMemory || 8) <= 2;
  CACHED = {
    ready: true,
    webgl: gl && fine && wide && !reduced && !lowPower,
    // Hero background film runs anywhere motion is welcome — desktop, phone,
    // tablet. Only reduced-motion opts out. The heavier LivingImage + ambient
    // grids stay behind the tighter `webgl` gate (desktop-only) so mobile
    // doesn't decode 12 clips at once.
    heroFilm: !reduced,
    fine,
    reduced,
  };
  CACHED_READY = true;
  waiters.forEach((f) => f(CACHED));
  waiters.clear();
}

// Decides once per page-load whether the WebGL layer runs.
// Mobile, coarse pointers, reduced-motion and low-power devices get the
// CSS/Framer treatment instead of a stuttering canvas.
export function useCapabilities() {
  const [caps, setCaps] = useState(CACHED_READY ? CACHED : initial);

  useEffect(() => {
    if (CACHED_READY) {
      setCaps(CACHED);
      return;
    }
    // schedule detection once, but every hook joins the same wait list
    if (waiters.size === 0) {
      setTimeout(detect, 0);
    }
    const cb = (v) => setCaps(v);
    waiters.add(cb);
    return () => waiters.delete(cb);
  }, []);

  return caps;
}
